/**
 * visa/planning.ts — pure travel-planning helpers for the Roamer Visa™.
 *
 * Suggestions come exclusively from the user's visa state and RECORDED
 * country knowledge — never invented destinations or made-up rules. Every
 * plan carries the legal disclaimer.
 */

import {
  LEGAL_DISCLAIMER,
  type CountryKnowledge,
  type TravelPlan,
  type VisaRecord,
} from './models';
import { extensionWindowOpen } from './reminders';

const DAY = 24 * 60 * 60 * 1000;

/** Leave this many days of slack before a visa expires. */
export const DEPARTURE_BUFFER_DAYS = 3;

export function buildTravelPlan(input: {
  visa: VisaRecord | null;
  knowledge: CountryKnowledge[];
  /** Countries the user prefers (injectable — e.g. from OCC config). */
  preferredCountries: string[];
  now: number;
}): TravelPlan {
  const { visa, knowledge, preferredCountries, now } = input;

  const suggestedDepartureAt = visa
    ? Math.max(now, visa.expiresAt - DEPARTURE_BUFFER_DAYS * DAY)
    : null;

  let suggestedExtensionAt: number | null = null;
  if (visa && visa.extensionEligible && visa.extensionWindowDays != null) {
    const windowStart = visa.expiresAt - visa.extensionWindowDays * DAY;
    suggestedExtensionAt = extensionWindowOpen(visa, now) === true ? now : Math.max(now, windowStart);
    if (now >= visa.expiresAt) suggestedExtensionAt = null; // too late — not pretended otherwise
  }

  // Recommendations only from countries with recorded knowledge.
  const candidates = knowledge.filter((ck) => ck.country !== visa?.country);
  const recommendedDestinations: TravelPlan['recommendedDestinations'] = [];
  for (const ck of candidates) {
    const preferred = preferredCountries.includes(ck.country);
    const easy = ck.entryEase != null && ck.entryEase >= 4;
    if (!preferred && !easy) continue;
    const bestOption = ck.visaOptions[0] ?? null;
    recommendedDestinations.push({
      country: ck.country,
      reason: [
        preferred ? 'matches your preferences' : null,
        easy ? 'easier entry for your passport' : null,
        bestOption ? `${bestOption.type} option on record` : null,
      ].filter(Boolean).join('; '),
      provenance: bestOption?.provenance ?? 'historical',
    });
  }
  recommendedDestinations.sort((a, b) => a.country.localeCompare(b.country));

  const easierEntryCountries = candidates
    .filter((ck) => ck.entryEase != null && ck.entryEase >= 4)
    .sort((a, b) => (b.entryEase ?? 0) - (a.entryEase ?? 0))
    .map((ck) => ck.country);

  return {
    suggestedDepartureAt,
    suggestedExtensionAt,
    recommendedDestinations,
    easierEntryCountries,
    disclaimer: LEGAL_DISCLAIMER,
  };
}
